// Production queues, auto-produce, rally, upgrades (§16).

import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist, vec } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Building } from '../state';
import { buildingPassives, maybeEchoSquad } from './annexes';

const dt = TUNING.dt;

/** Strongest nearby training aura (Field Bastion trainSpeedAura); 1 when none. */
function trainAuraMult(sim: Sim, b: Building): number {
  let mult = 1;
  for (const other of sim.state.buildings.values()) {
    if (other.owner !== b.owner || other.state !== 'active' || other.id === b.id) continue;
    for (const passive of buildingPassives(buildingDef(other.defId))) {
      if (passive.kind !== 'trainSpeedAura') continue;
      if (dist(other.pos, b.pos) <= passive.radius) mult = Math.max(mult, passive.mult);
    }
  }
  return mult;
}

export function stepProduction(sim: Sim): void {
  const st = sim.state;
  for (const b of st.buildings.values()) {
    if (b.state !== 'active') continue;
    const bd = buildingDef(b.defId);

    if (b.captureTrainingRemaining > 0) b.captureTrainingRemaining = Math.max(0, b.captureTrainingRemaining - dt);
    for (const key of Object.keys(b.traitCooldowns)) {
      if (b.traitCooldowns[key] > 0) b.traitCooldowns[key] = Math.max(0, b.traitCooldowns[key] - dt);
    }

    // upgrade research — runs for EVERY active building (site towers have no roster;
    // ticking this after the roster check left their research stuck forever)
    if (b.upgradeRemaining > 0) {
      b.upgradeRemaining -= dt * sim.annexMult(b.owner, 'upgradeSpeedMult'); // Royal Works
      if (b.upgradeRemaining <= 0) {
        b.upgradeRemaining = 0;
        b.upgradeDone = true;
        sim.pushEvent({
          type: 'UpgradeComplete',
          id: b.id,
          owner: b.owner,
          pos: { ...b.pos },
          name: bd.upgrade?.name ?? 'Upgrade',
        });
      }
    }

    // Bastion annex research (multi-upgrade track)
    if (b.upgradeInProgress) {
      b.upgradeInProgress.remaining -= dt;
      if (b.upgradeInProgress.remaining <= 0) {
        const id = b.upgradeInProgress.id;
        b.upgradeInProgress = null;
        b.upgradesDone.push(id);
        const up = bd.upgrades?.find((u) => u.id === id);
        sim.pushEvent({
          type: 'UpgradeComplete',
          id: b.id,
          owner: b.owner,
          pos: { ...b.pos },
          name: up?.name ?? 'Annex',
        });
        // apply typed annex effects (D2): one-shot bonuses here; multipliers are
        // queried live via sim.annexMult at their use sites
        const p = st.players[b.owner];
        for (const eff of up?.effects ?? []) {
          if (eff.kind === 'shelterBonus') p.shelterCapacity += eff.amount;
        }
        sim.recomputeCaps(b.owner);
      }
    }

    if (!bd.roster) continue;

    // Carrion Incubation: every newly observed death can cut the active queue, but
    // each item has a deterministic cap so a single battle cannot finish it outright.
    const deathTrait = bd.traits?.find((t) => t.kind === 'deathProduction');
    if (deathTrait && b.queue[0]) {
      let reduced = b.traitCounters.deathProduction ?? 0;
      let newest = b.lastTraitDeathAt;
      for (const death of st.recentDeaths) {
        if (death.at <= b.lastTraitDeathAt || dist(death.pos, b.pos) > deathTrait.radius) continue;
        const cut = Math.min(deathTrait.secondsPerDeath, deathTrait.maxSecondsPerQueueItem - reduced);
        if (cut > 0) {
          b.queue[0].remaining = Math.max(0, b.queue[0].remaining - cut);
          reduced += cut;
        }
        b.traitCounters.nearDeaths = (b.traitCounters.nearDeaths ?? 0) + 1;
        newest = Math.max(newest, death.at);
      }
      b.traitCounters.deathProduction = reduced;
      b.lastTraitDeathAt = newest;
    }

    // auto-produce: keep the queue fed round-robin among enabled rows (§16.2)
    if (b.queue.length < (bd.roster.queueSize ?? 5)) {
      for (const entry of bd.roster.entries) {
        if (!b.autoProduce[entry.unitId]) continue;
        const p = st.players[b.owner];
        const cmd = entry.cost.command ?? 0;
        if (p.commandUsed + cmd > p.commandCap) {
          // hitting command cap turns auto OFF for the row (§16.2)
          b.autoProduce[entry.unitId] = false;
          continue;
        }
        // only one queued copy per auto row at a time (keeps rows fair)
        const queuedOfRow = b.queue.filter((q) => q.unitId === entry.unitId).length;
        if (queuedOfRow >= 2) continue;
        sim.tryEnqueueUnit(b, entry.unitId);
      }
    }

    // advance head of queue
    if (b.queue.length > 0) {
      const head = b.queue[0];
      // High Command Hall (trainSpeedMult) and Field Bastion aura speed all training
      let buildingMult = 1;
      const safeMuster = bd.traits?.find((t) => t.kind === 'safeMuster');
      const safeMusterReady = !!safeMuster && st.time - Math.max(b.lastDamagedAt, b.traitCounters.safeMusterAt ?? 0) >= safeMuster.safeSeconds;
      if (safeMusterReady) buildingMult *= safeMuster.trainMult;
      const captureTraining = bd.traits?.find((t) => t.kind === 'captureTraining');
      if (captureTraining && b.captureTrainingRemaining > 0) buildingMult *= captureTraining.trainMult;
      const remote = bd.traits?.find((t) => t.kind === 'remoteDeployment');
      if (remote && (!remote.requiresMode || b.modeActive) && !b.upgradeDone) buildingMult /= remote.extraTrainMult;
      head.remaining -= dt * sim.annexMult(b.owner, 'trainSpeedMult') * trainAuraMult(sim, b) * buildingMult;
      // Convergence Protocol: the synchronized network trains 20% faster
      if (st.players[b.owner].convergenceRemaining > 0) head.remaining -= dt * 0.2;
      // Cruel Production (Blood Foundry): opt-in worker-blood acceleration — 25% faster
      // while a nearby worker above 40% health bleeds for it
      if (head.remaining <= 0) {
        b.queue.shift();
        b.traitCounters.deathProduction = 0;
        let door = vec(b.pos.x, b.pos.y - bd.footprint.h / 2 - 1.5);
        if (remote && (!remote.requiresMode || b.modeActive)) {
          const exits = [...st.connectedConduits]
            .map((id) => st.buildings.get(id))
            .filter((x): x is Building => !!x && x.owner === b.owner && x.id !== b.id && x.state === 'active')
            .sort((a, c) => dist(a.pos, b.rally) - dist(c.pos, b.rally) || a.id - c.id);
          const exit = exits[0];
          if (exit) {
            const ed = buildingDef(exit.defId);
            door = vec(exit.pos.x, exit.pos.y - ed.footprint.h / 2 - 1.5);
          }
        }
        if (unitDef(head.unitId).workerVariant) {
          // worker variants (Master Builder, Barkwright) join the worker economy
          sim.spawnWorker(b.owner, door, head.unitId);
        } else {
          const squad = sim.spawnSquad(head.unitId, b.owner, door);
          if (deathTrait && b.upgradeDone && (b.traitCounters.nearDeaths ?? 0) >= 10) {
            const brood = sim.spawnSquad(head.unitId, b.owner, door, {
              maxHealthMult: 0.5,
              temporarySeconds: 40,
              commandFree: true,
            });
            sim.sendSpawnToRally(brood, b);
            b.traitCounters.nearDeaths = 0;
          }
          if (bd.cruelProduction && (b.traitCounters.bloodArmor ?? 0) > 0) {
            squad.tempHealth = Math.max(squad.tempHealth, b.upgradeDone ? 500 : 300); // TEST
            squad.tempHealthRemaining = 20; // TEST
            b.traitCounters.bloodArmor = 0;
          }
          // move to rally with defend-at-point behavior (A-22)
          sim.sendSpawnToRally(squad, b);
          // High Command Hall: fresh squads march faster to their rally point
          const fresh = sim.annexEffect(b.owner, 'freshSquadSpeed');
          if (fresh) {
            squad.buffs.push({ stat: 'moveSpeed', mult: fresh.mult, remaining: fresh.duration, source: 'fresh-squad' });
          }
          if (safeMuster && safeMusterReady) {
            squad.buffs.push({ stat: 'moveSpeed', mult: safeMuster.marchMult, remaining: safeMuster.marchSeconds, source: 'ordered-muster' });
            b.traitCounters.safeMusterAt = st.time;
          }
          if (remote && b.upgradeDone && remote.arrivalShield > 0) {
            squad.tempHealth = Math.max(squad.tempHealth, remote.arrivalShield);
            squad.tempHealthRemaining = 8; // TEST
          }
          for (const trait of bd.traits ?? []) {
            if (trait.kind !== 'producedBuff' || (trait.upgradeOnly && !b.upgradeDone)) continue;
            const n = (b.traitCounters.produced ?? 0) + 1;
            b.traitCounters.produced = n;
            if (n % trait.every === 0) {
              squad.buffs.push({ stat: trait.stat, mult: trait.mult, remaining: trait.duration, source: `building-${b.id}` });
            }
          }
          // Echo Observatory: duplicate the squad if it finished at a connected location
          maybeEchoSquad(sim, b.owner, head.unitId, door);
        }
        sim.recomputeCaps(b.owner);
      }
    }
  }
}
